/**
 * Snooze ladder for reviews and email drafts — pure helpers.
 *
 * Durations are Fibonacci days; the labels simplify to weeks and months as
 * the numbers grow, but every option also names its concrete return date.
 * The server is authoritative (`services/review_snooze.py`): it validates
 * the rung and computes the return instant, 00:00 Berlin on today + N.
 */
import { addDays, berlinDate } from "./focusTime";

// Mirror: system/services/review_snooze.py :: SNOOZE_LADDER
export const SNOOZE_LADDER = [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144, 233, 377] as const;

const LABELS: Record<number, string> = {
  1: "1 day",
  2: "2 days",
  3: "3 days",
  5: "5 days",
  8: "1 week",
  13: "2 weeks",
  21: "3 weeks",
  34: "1 month",
  55: "2 months",
  89: "3 months",
  144: "5 months",
  233: "8 months",
  377: "1 year",
};

export function snoozeLabel(days: number): string {
  return LABELS[days] ?? `${days} days`;
}

/** Berlin calendar date (YYYY-MM-DD) a snooze of `days` picked now returns on. */
export function returnDate(days: number, nowMs: number): string {
  return addDays(berlinDate(nowMs), days);
}

const DAY_MONTH = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const DAY_MONTH_YEAR = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});

/** "Tue 21 Oct", with the year when it differs from today's. */
export function formatReturnDate(date: string, nowMs: number): string {
  const instant = new Date(`${date}T00:00:00Z`);
  const sameYear = date.slice(0, 4) === berlinDate(nowMs).slice(0, 4);
  return (sameYear ? DAY_MONTH : DAY_MONTH_YEAR).format(instant).replace(",", "");
}

/** Server timestamps may arrive without an offset (naive UTC) — read them as UTC. */
export function parseUtc(raw: string): number {
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : `${raw}Z`);
}

/** Berlin date a stored `snoozed_until` instant falls on. */
export function snoozedUntilDate(snoozedUntil: string): string {
  return berlinDate(parseUtc(snoozedUntil));
}

/** The rung after `lastDays` — the escalation the picker preselects. */
export function nextRung(lastDays: number | null | undefined): number {
  if (!lastDays) return SNOOZE_LADDER[0];
  return SNOOZE_LADDER.find((d) => d > lastDays) ?? SNOOZE_LADDER[SNOOZE_LADDER.length - 1];
}

/**
 * Preselected rung for a row: while it is still snoozed the picker corrects
 * the current deferral (same rung); otherwise it escalates one step.
 */
export function defaultRung(doc: { queue?: string | null; snooze_days?: number | null }): number {
  if (doc.queue === "snoozed" && doc.snooze_days) return doc.snooze_days;
  return nextRung(doc.snooze_days);
}
